import { ProductType } from 'cart-model';
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router';
import { logChooseInvestment } from '../../model/events';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import IkzeExtendedEntrepreneurDepositAgreement, { IkzeExtendedEntrepreneurDepositAgreementForm }
  from '../IkzeExtendedEntrepreneurDeposit/IkzeExtendedEntrepreneurDepositAgreement';
import { ProductWizardTitle } from '../ProductWizard/ProductWizardTitle/ProductWizardTitle';
import { getDynamicTitleKey } from '../ProductWizard/ProductWizardTitle/getDynamicTitleKey';
import { ChooseInvestmentSliders } from './ChooseInvestmentSliders/ChooseInvestmentSliders';
import { validateLimits } from './LimitsValidation/LimitsValidation';
import { Button } from 'ui-components';
import { useTransRedirect } from 'common-react';
import Screen from '../ProductWizard/Screen';

import useStyles from './ChooseInvestmentAmount.style';

const ChooseInvestmentAmount: FunctionComponent = () => {
  const c = useStyles({});

  const isEntrepreneur = useSelector((state: ProductWizardState) => state.cartState.isEntrepreneur);
  const entrepreneurNip = useSelector((state: ProductWizardState) => state.cartState.entrepreneurNip);
  const currentLimits = useSelector((state: ProductWizardState) => state.apiState.currentLimits);
  const productType = useSelector((state: ProductWizardState) => state.uiState.productType);
  const initialAmountState = useSelector((state: ProductWizardState) => state.uiState.initialAmount);
  const monthlyAmountState = useSelector((state: ProductWizardState) => state.uiState.monthlyAmount);
  const [initialAmount, setInitialAmount] = useState(initialAmountState);
  const [monthlyAmount, setMonthlyAmount] = useState(monthlyAmountState);
  const [isIkzeExtendedFormValid, setIkzeExtendedFormValid] = useState(true);
  const [ikzeExtendedFormData, setIkzeExtendedFormData] = useState<IkzeExtendedEntrepreneurDepositAgreementForm>({
    isEntrepreneur: false,
    nip: undefined,
    entrepreneurConsent: undefined,
  });
  const dispatch = useDispatch();
  const [t] = useTranslation('choose-investment-amount');
  const redirect = useTransRedirect();

  if (productType === undefined) {
    return <Redirect to="/"/>;
  }

  const limitsValidation = validateLimits(productType, currentLimits, initialAmount, monthlyAmount, ikzeExtendedFormData.isEntrepreneur);

  const chooseInvestmentAmount = () => {
    dispatch(productWizardActions.setInitialAmount(initialAmount));
    dispatch(productWizardActions.setMonthlyAmount(monthlyAmount));
    if (productType === ProductType.ikze) {
      dispatch(productWizardActions.setIsEntrepreneur(ikzeExtendedFormData.isEntrepreneur));
      dispatch(productWizardActions.setEntrepreneurNip(ikzeExtendedFormData.isEntrepreneur
                                                        ? ikzeExtendedFormData.nip
                                                        : undefined));
    }
    logChooseInvestment();
  };

  const handleShowResults = () => {
    chooseInvestmentAmount();
    redirect(Screen.SELECT_RECOMMENDED_FUNDS.code);
  }

  return (
    <>
      <ProductWizardTitle
      titleKey={getDynamicTitleKey(productType, 'choose-investment')}
      subtitle={t('subheader', 'Amount chosen in this step does not oblige you to any payments.')}
      />
      <div className={c.container}>
        {productType === ProductType.ikze &&
          <IkzeExtendedEntrepreneurDepositAgreement
            initialValues={{
              isEntrepreneur: isEntrepreneur ? isEntrepreneur : false,
              nip: isEntrepreneur ? entrepreneurNip : undefined,
              entrepreneurConsent: isEntrepreneur ? true : undefined,
            }}
            handleChange={values => setIkzeExtendedFormData(values)}
            handleValid={valid => setIkzeExtendedFormValid(valid)}
          />
        }
        <ChooseInvestmentSliders initialAmount={initialAmount}
                                 monthlyAmount={monthlyAmount}
                                 onInitialAmountChange={setInitialAmount}
                                 onMonthlyAmountChange={setMonthlyAmount}
                                 limitsValidation={limitsValidation}
                                 productType={productType}/>
        <div className={c.buttonWrapper}>
          <Button
            color="secondary"
            accent
            testId="choose-investment-submit-button"
            onClick={handleShowResults}
            disabled={!limitsValidation.isValid || !isIkzeExtendedFormValid}
            >
            {t('submit', 'Show results')}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ChooseInvestmentAmount;
